import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Search, Check } from 'lucide-react';
import { colors } from '../theme/colors';

// Container for the entire component
const Container = styled.div`
  position: relative;
  width: 100%;
`;

// Main input field
const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${props => props.isDark ? '#555' : '#d1d5db'};
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${props => props.isDark ? '#2d2d2d' : 'white'};
  color: ${props => props.isDark ? '#f0f0f0' : '#374151'};
  
  &::placeholder {
    color: ${props => props.isDark ? '#a0a0a0' : '#9ca3af'};
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

// Search icon positioned in input
const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.isDark ? '#a0a0a0' : '#9ca3af'};
  display: flex;
  align-items: center;
  pointer-events: none;
`;

// Dropdown list container
const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.isDark ? '#3a3a3a' : 'white'};
  border: 1px solid ${props => props.isDark ? '#555' : '#d1d5db'};
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10000;
  margin-top: 0.25rem;
  
  /* Scrollbar styling based on dark/light mode */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.isDark ? '#2a2a2a' : '#f1f1f1'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.isDark ? '#4a4a4a' : '#c1c1c1'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.isDark ? '#5a5a5a' : '#a8a8a8'};
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.isDark ? '#4a4a4a #2a2a2a' : '#c1c1c1 #f1f1f1'};
`;

// Individual dropdown item
const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: ${props => props.isDark ? '#f0f0f0' : '#374151'};
  
  &:hover {
    background: ${props => props.isDark ? '#4a4a4a' : '#f3f4f6'};
  }
  
  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`;

// Container for selected tags
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  min-height: ${props => props.hasItems ? 'auto' : '0'};
`;

// Individual tag/chip
const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: ${props => props.isDark ? colors.chip.dark.background : colors.chip.light.background};
  border: 1px solid ${props => props.isDark ? colors.chip.dark.border : colors.chip.light.border};
  border-radius: 6px;
  font-size: 0.8rem;
  color: ${props => props.isDark ? colors.chip.dark.text : colors.chip.light.text};
  animation: slideIn 0.2s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// Remove button in tag
const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isDark ? colors.chip.dark.removeButton : colors.chip.light.removeButton};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  
  &:hover {
    color: ${props => props.isDark ? colors.chip.dark.removeButtonHover : colors.chip.light.removeButtonHover};
  }
`;


// Check icon for selected items
const CheckIcon = styled.div`
  color: #10b981;
  display: flex;
  align-items: center;
`;

/**
 * Stable Multi-Select Typeahead Component
 * 
 * This is a dedicated, stable component that should not be modified frequently.
 * It handles multi-selection with search functionality.
 * 
 * Props:
 * - items: Array of items to select from
 * - selectedItems: Array of currently selected items
 * - onSelectionChange: Callback when selection changes
 * - placeholder: Placeholder text for input
 * - getItemLabel: Function to get display label from item
 * - getItemValue: Function to get unique value from item
 * - maxVisibleTags: Maximum number of tags to show (default: 5)
 * - isDark: Whether to use dark theme (default: false)
 */
const StableTypeahead = ({ 
  items = [], 
  selectedItems = [], 
  onSelectionChange,
  placeholder = "Search and select items...",
  getItemLabel = (item) => item.name || item.label || item,
  getItemValue = (item) => item.id || item.value || item,
  maxVisibleTags = 5,
  isDark = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Debug logging
  // console.log('🔍 StableTypeahead props:', { items: items.length, selectedItems: selectedItems.length, searchTerm, isOpen });

  // Filter items based on search term
  const filteredItems = items.filter(item => {
    const label = getItemLabel(item).toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });
  
  // console.log('🔍 Filtered items:', filteredItems.length);

  // Helper function to check if item is selected
  const isItemSelected = (item) => {
    return selectedItems.some(selected => 
      getItemValue(selected) === getItemValue(item)
    );
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    const newSelection = [...selectedItems, item];
    onSelectionChange(newSelection);
    setSearchTerm('');
    setIsOpen(false); // Close dropdown after selection
    inputRef.current?.focus();
  };

  // Handle item removal
  const handleItemRemove = (itemToRemove) => {
    const newSelection = selectedItems.filter(item => 
      getItemValue(item) !== getItemValue(itemToRemove)
    );
    onSelectionChange(newSelection);
  };

  // Handle clear all
  const handleClearAll = () => {
    onSelectionChange([]);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && searchTerm === '' && selectedItems.length > 0) {
      // Remove last selected item
      handleItemRemove(selectedItems[selectedItems.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Calculate visible and hidden tags
  const visibleTags = selectedItems.slice(0, maxVisibleTags);
  const hiddenCount = Math.max(0, selectedItems.length - maxVisibleTags);

  return (
    <Container ref={containerRef}>
      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <SearchIcon isDark={isDark}>
          <Search size={16} />
        </SearchIcon>
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          isDark={isDark}
        />
        
        {/* Dropdown List */}
        {isOpen && filteredItems.length > 0 && (
          <DropdownList isDark={isDark}>
            {filteredItems.slice(0, 10).map((item) => {
              const selected = isItemSelected(item);
              return (
                <DropdownItem
                  key={getItemValue(item)}
                  onClick={() => selected ? handleItemRemove(item) : handleItemSelect(item)}
                  isDark={isDark}
                  style={{ 
                    background: selected ? (isDark ? 'transparent' : '#f0f9ff') : undefined,
                    fontWeight: selected ? '600' : 'normal'
                  }}
                >
                  <span>{getItemLabel(item)}</span>
                  {selected && (
                    <CheckIcon isDark={isDark}>
                      <Check size={16} />
                    </CheckIcon>
                  )}
                </DropdownItem>
              );
            })}
            {filteredItems.length > 10 && (
              <DropdownItem isDark={isDark} style={{ color: '#6b7280', fontStyle: 'italic' }}>
                ... and {filteredItems.length - 10} more
              </DropdownItem>
            )}
          </DropdownList>
        )}
      </div>

      {/* Selected Tags */}
      {selectedItems.length > 0 && (
        <TagsContainer hasItems={selectedItems.length > 0}>
          {visibleTags.map((item) => (
            <Tag key={getItemValue(item)} isDark={isDark}>
              <span>{getItemLabel(item)}</span>
              <RemoveButton onClick={() => handleItemRemove(item)} isDark={isDark}>
                <X size={14} />
              </RemoveButton>
            </Tag>
          ))}
          {hiddenCount > 0 && (
            <Tag 
              isDark={isDark}
              style={{ 
                background: isDark ? colors.chip.dark.background : colors.gray100, 
                color: isDark ? colors.chip.dark.removeButton : colors.gray500, 
                border: isDark ? `1px solid ${colors.chip.dark.border}` : `1px solid ${colors.gray100}` 
              }}
            >
              + {hiddenCount} more
            </Tag>
          )}
        </TagsContainer>
      )}

    </Container>
  );
};

export default StableTypeahead;
